import { lazy } from 'react';
import type { ComponentType, LazyExoticComponent } from 'react';

/**
 * Key marking that this tab has already spent its one reload recovering a
 * chunk that would not load. Session-scoped so a fresh tab starts with one
 * again, and cleared as soon as a page does load.
 */
const RELOAD_KEY = 'iqd-next:chunk-reload';

/**
 * Reading storage throws in a few embedded contexts. Without it there is no
 * way to tell a first failure from a second, so treat the reload as already
 * spent — a wrong "no" costs a page of error text, a wrong "yes" costs an
 * endless reload loop.
 */
function reloadSpent(): boolean {
  try {
    return sessionStorage.getItem(RELOAD_KEY) !== null;
  } catch {
    return true;
  }
}

function spendReload() {
  try {
    sessionStorage.setItem(RELOAD_KEY, '1');
  } catch {
    // Unreadable storage already counts as spent, so nothing to record.
  }
}

function releaseReload() {
  try {
    sessionStorage.removeItem(RELOAD_KEY);
  } catch {
    // As above — a tab that cannot store the flag never reloads on it.
  }
}

/**
 * `lazy`, plus recovery for a chunk that is no longer on the CDN.
 *
 * Assets are hashed and deploys sync them with `--delete` on staging, so a tab
 * left open across a deploy asks for a filename that has stopped existing and
 * the route dies on "Failed to fetch dynamically imported module". The running
 * page cannot be talked out of the name it holds; only a reload can, because
 * index.html and the remote entry are served `no-cache` and come back naming
 * the new chunks. One reload per tab, released once a page loads, so a chunk
 * that fails for any other reason surfaces to the error boundary instead of
 * cycling.
 */
export type PreloadablePage<T extends ComponentType<Record<string, never>>> =
  LazyExoticComponent<T> & {
    /**
     * Starts the chunk without rendering anything.
     *
     * The raw loader, deliberately, not the recovering one below: a hover is
     * not worth reloading the page over. A chunk that has gone missing stays
     * missing until someone actually navigates, and the recovery runs then.
     */
    preload: () => void;
  };

export function lazyPage<T extends ComponentType<Record<string, never>>>(
  load: () => Promise<{ default: T }>,
): PreloadablePage<T> {
  const page = lazy(async () => {
    try {
      const module = await load();
      releaseReload();
      return module;
    } catch (error) {
      if (reloadSpent()) {
        throw error;
      }
      spendReload();
      window.location.reload();
      // A reload is not instant. Resolving — or rejecting — here would flash a
      // page or an error message in the moment before it lands.
      return new Promise<never>(() => undefined);
    }
  }) as PreloadablePage<T>;

  page.preload = () => {
    load().catch(() => undefined);
  };

  return page;
}
